import { useContext, useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Typography,
  useTheme,
} from '@mui/material';
import { TrendingDown, TrendingUp } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { AuthContext } from 'contexts/AuthContext';
import { DropsContext } from 'contexts/DropsContext';
import VirtualDrop from 'components/VirtualDrop';
import Container from 'components/Container';
import Header from 'components/Header';
import Button from 'components/Button';
import Input from 'components/Input';
import Blank from 'assets/blank.png';
import api from 'services/api';
import styles from './styles';
import moment from 'moment';

const Item = ({ item, style }) => {
  const props = {
    item: true,
    xl: 2.4,
    lg: 3,
    md: 4,
    sm: 6,
    xs: 12,
    display: 'flex',
  };
  const color = item?.quantidade > 0 ? 'success' : 'error';
  const label = item?.quantidade + ' ' + (item?.um || '');
  const icon = item?.quantidade > 0 ? <TrendingUp /> : <TrendingDown />;
  const lines = [
    {
      label: 'Código',
      value: item?.codigo,
    },
    {
      label: 'Referência',
      value: item?.referencia,
    },
    {
      label: 'Variação',
      value: item?.variacao,
    },
    {
      label: 'Rastreabilidade',
      value: item?.rastreabilidade,
    },
    {
      label: 'Validade',
      value: item?.validade && moment(item?.validade).format('DD/MM/YYYY'),
    },
  ];

  return (
    <Grid {...props}>
      <Card sx={style?.card}>
        <CardMedia
          component="img"
          src={item?.imagem || Blank}
          alt={item?.descricao}
          sx={style?.image}
        />
        <CardContent sx={style?.cardContent}>
          <Typography variant="body1" fontWeight="bold">
            {item?.descricao}
          </Typography>
          <Box display="flex" justifyContent="flex-end" my={1}>
            <Chip
              icon={icon}
              label={label}
              color={color}
              size="small"
              variant="outlined"
            />
          </Box>
          {lines?.map(
            ({ label, value }, i) =>
              Boolean(value) && (
                <Box key={i?.toString()} display="flex">
                  <Typography
                    flex={1}
                    variant="caption"
                    fontWeight="bold"
                    color="text.secondary"
                  >
                    {label}:{' '}
                  </Typography>
                  <Typography flex={1} variant="caption" color="text.secondary">
                    {value}
                  </Typography>
                </Box>
              )
          )}
        </CardContent>
      </Card>
    </Grid>
  );
};

const Inventario = () => {
  const { empresa } = useContext(AuthContext);
  const { drops } = useContext(DropsContext);
  const defaultValues = {
    page: 0,
    size: 50,
    entidade_id: empresa,
    produto_id: null,
    referencia: '',
  };
  const { control, handleSubmit, setValue, getValues } = useForm({
    defaultValues,
  });
  const [produtos, setProdutos] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const style = styles(theme);

  const loadData = async (params) => {
    if (loading) return;
    try {
      setLoading(true);
      const { data } = await api.get(`/WMS/Estoque/Inventario`, { params });
      setProdutos((prev) => (params?.page === 0 ? data : [...prev, ...data]));
      setHasMore(data?.length === params?.size);
      setValue('page', params?.page);
    } catch (error) {
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (values) => {
    loadData({ ...values, page: 0 });
  };

  const onChangePage = (values) => {
    const { page } = getValues();
    const nextPage = (page || 0) + 1;
    loadData({ ...values, page: nextPage });
  };

  useEffect(() => {
    handleSubmit(loadData)();
  }, []);

  return (
    <Container>
      <Grid container spacing={2} sx={style?.container}>
        <Grid item xs={12} md={3} sx={style?.filterContainer}>
          <form style={style?.flexContainer} onSubmit={handleSubmit(onSubmit)}>
            <Typography variant="h5">Filtros</Typography>
            <Divider />
            <VirtualDrop
              label="Produto"
              control={control}
              name="produto_id"
              options={drops?.Produto?.filter(
                (f) => f?.entidade_id === empresa
              )}
            />
            <Input control={control} label="Referência" name="referencia" />
            <Button
              type="submit"
              loading={loading}
              onClick={() => {}}
              variant="contained"
              sx={style?.formButton}
            >
              Filtrar
            </Button>
            <Divider />
          </form>
        </Grid>
        <Grid item xs={12} md={9} sx={style?.contentContainer}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Header titulo="Inventário" />
            </Grid>
            {!produtos?.length && !loading && !hasMore && (
              <Grid item xs={12} textAlign="center">
                <Typography variant="caption">
                  Nenhum produto encontrado.
                </Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              {loading && <LinearProgress />}
            </Grid>
            {produtos?.map((item, index) => (
              <Item item={item} key={index?.toString()} />
            ))}
            {hasMore && (
              <Grid item xs={12} textAlign="center" pb={2}>
                <Button
                  loading={loading}
                  variant="contained"
                  onClick={handleSubmit(onChangePage)}
                >
                  Carregar mais
                </Button>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Inventario;
